/**
 * Stale-chunk recovery.
 *
 * Every route is a hashed, lazily loaded chunk. After a deploy, a tab that was
 * opened before it asks for chunk files that no longer exist, so the dynamic
 * import rejects. The fix is a reload (which fetches the new index.html and the
 * new hashes) — but only once, or a genuinely broken deploy would reload-loop.
 */

const RELOAD_KEY = 'cbcrm.chunkReloadAt'
const RELOAD_WINDOW_MS = 30_000

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i,
  /ChunkLoadError/i,
  /Loading chunk [\w-]+ failed/i,
  /Loading CSS chunk [\w-]+ failed/i,
]

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false
  const name = (error as { name?: string }).name ?? ''
  const message = (error as { message?: string }).message ?? String(error)
  if (name === 'ChunkLoadError') return true
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message))
}

/**
 * Reload the page once per RELOAD_WINDOW_MS. Returns true when a reload was
 * triggered, false when one already happened recently (caller should surface
 * the error instead of looping).
 */
export function reloadOnceForStaleChunk(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY) ?? 0)
    if (last && Date.now() - last < RELOAD_WINDOW_MS) return false
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
  } catch {
    // sessionStorage unavailable (private mode): fall through and reload once
    // per call; the error boundary only calls this once per caught error.
  }
  window.location.reload()
  return true
}
